import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Sentry } from './sentry';
import Layout from './Layout';
import { isSafeAuthCallbackUrl } from './utils/authCallbackUrl';
import MemoIconHomebotApp from './icons/IconHomebotApp.js';
import MemoIconClientRetention from './icons/IconClientRetention.js';
import MemoIconDatabaseGrowth from './icons/IconDatabaseGrowth.js';
import MemoIconAgentPartnerships from './icons/IconAgentPartnerships.js';
import MemoIconLeadGeneration from './icons/IconLeadGeneration.js';
import './SignUp.css'; // Reuse existing styles

// Extract auth token from the callback URL
const extractAuthToken = (url) => {
  if (!url) return null;
  try {
    const urlObj = new URL(url);
    
    // Try to get token from URL fragment (hash) - e.g., #access_token=TOKEN
    if (urlObj.hash) {
      const hashParams = new URLSearchParams(urlObj.hash.substring(1)); // Remove the '#' prefix
      const hashToken = hashParams.get('access_token');

      if (hashToken) return hashToken;
    }

    return null;
  } catch (error) {
    console.error('Error extracting auth token:', error);
    Sentry.captureException(error);
    return null;
  }
};

const Success = () => {
  const location = useLocation();
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [isSubmittingGoal, setIsSubmittingGoal] = useState(false);
  // SignUp hands the callback over in router state, never the query string, so the
  // token-bearing URL stays out of the address bar and page-view tracking.
  const stateAuthUrl = location.state?.authUrl;
  const authCallbackUrl = isSafeAuthCallbackUrl(stateAuthUrl) ? stateAuthUrl : null;

  // Report a rejected or missing callback without the URL itself, which carries access_token.
  useEffect(() => {
    if (authCallbackUrl) return;
    let host = null;
    try {
      host = new URL(stateAuthUrl).hostname;
    } catch {
      // not a parseable URL
    }
    Sentry.captureMessage('Success: auth callback missing or rejected', {
      level: 'warning',
      extra: { hadState: stateAuthUrl != null, host }
    });
  }, [authCallbackUrl, stateAuthUrl]);
  const marketingUrl = process.env.REACT_APP_MARKETING_URL;

  const redirectToHomebot = (url) => {
    window.location.href = url;
  };

  // 5-second idle timer
  useEffect(() => {
    if (!authCallbackUrl) return;

    let timer = setTimeout(() => {
      redirectToHomebot(authCallbackUrl);
    }, 5000);

    // Reset timer on user activity
    const handleUserActivity = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        redirectToHomebot(authCallbackUrl);
      }, 10000);
    };

    // Listen for user interactions
    window.addEventListener('click', handleUserActivity);
    window.addEventListener('keydown', handleUserActivity);
    window.addEventListener('mousemove', handleUserActivity);
    window.addEventListener('scroll', handleUserActivity);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('click', handleUserActivity);
      window.removeEventListener('keydown', handleUserActivity);
      window.removeEventListener('mousemove', handleUserActivity);
      window.removeEventListener('scroll', handleUserActivity);
    };
  }, [authCallbackUrl]);

  const goals = [
    {
      id: 'client-retention',
      title: 'Client retention',
      icon: <MemoIconClientRetention />
    },
    {
      id: 'database-growth',
      title: 'Database growth',
      icon: <MemoIconDatabaseGrowth />
    },
    {
      id: 'agent-partnerships',
      title: 'Agent partnerships',
      icon: <MemoIconAgentPartnerships />
    },
    {
      id: 'lead-generation',
      title: 'Lead generation',
      icon: <MemoIconLeadGeneration />
    }
  ];

  const handleGoalSelect = async (goalId) => {
    setSelectedGoal(goalId);
    if (isSubmittingGoal) return;

    const authToken = extractAuthToken(authCallbackUrl);

    // If we have an auth token, send the goal to the backend
    if (authToken) {
      setIsSubmittingGoal(true);
      
      try {
        const response = await fetch(`${process.env.REACT_APP_MIKASA_URL}/onboarding/goal`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            goal: goalId
          })
        });

        if (!response.ok) {
          console.error('Failed to submit goal:', await response.text());
        } else {
          console.log('Goal submitted successfully');
        }
      } catch (error) {
        console.error('Error submitting goal:', error);
        Sentry.captureException(error);
      } finally {
        setIsSubmittingGoal(false);
      }
    }
  };

  return (
    <Layout>
      <div className="mt-16 single-page-container">
        <div className="form-section-single text-center">
          <div className="flex justify-center mb-4">
            <MemoIconHomebotApp style={{ transform: 'scale(0.9)' }} />
          </div>
          
          <h1 className="mt-4 mb-2" style={{ 
            fontSize: '3rem', 
            fontWeight: 'bold', 
            color: '#F51E84',
            lineHeight: '1.1'
          }}>
            You're all set
          </h1>
          
          <h2 className="mt-0 mb-6" style={{ 
            fontSize: '3rem', 
            fontWeight: 'bold', 
            color: '#F51E84',
            lineHeight: '1.1'
          }}>
            Welcome to Homebot!
          </h2>
          
          <p className="mb-8" style={{ 
            fontSize: '1.2rem', 
            color: '#ffffff', 
            opacity: 0.9,
            maxWidth: '600px',
            margin: '0 auto 2rem'
          }}>
            Your payment was successfully processed! Thank you for choosing Homebot.
          </p>

          <div className="mt-8 mb-8">
            <p className="uppercase tracking-wide mb-4" style={{ 
              color: '#F51E84', 
              fontSize: '0.9rem',
              fontWeight: '600'
            }}>
              MAKE THE MOST OF HOMEBOT:
            </p>
            
            <h3 className="mb-8" style={{ 
              fontSize: '2.5rem', 
              fontWeight: 'bold', 
              color: '#ffffff',
              lineHeight: '1.1'
            }}>
              What's your #1 goal?
            </h3>

            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 1fr', 
              gap: '0.75rem', 
              maxWidth: '36rem', 
              margin: '0 auto 3rem' 
            }}>
              {goals.map((goal) => (
                <div
                  key={goal.id}
                  className={`capacity-option ${selectedGoal === goal.id ? 'selected' : ''}`}
                  onClick={() => handleGoalSelect(goal.id)}
                  tabIndex={0}
                  role="button"
                  style={{ 
                    position: 'relative',
                    cursor: 'pointer',
                    padding: '1.5rem 1rem',
                    minHeight: '100px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.75rem',
                    border: selectedGoal === goal.id ? '2px solid #F51E84' : '2px solid rgba(255, 255, 255, 0.08)',
                    backgroundClip: 'padding-box',
                    boxShadow: '0px 0px 1px rgba(0, 0, 0, 0.1), 0px 10px 15px -3px rgba(0, 0, 0, 0.3), 0px 4px 6px -2px rgba(0, 0, 0, 0.55)',
                    borderRadius: '10px',
                    backgroundColor: selectedGoal === goal.id ? '#25232B' : '#1E1C23',
                    outline: '1px solid transparent',
                    transition: 'transform 0.15s cubic-bezier(0.4,0,0.2,1), border-color 0.15s cubic-bezier(0.4,0,0.2,1), outline 0.15s cubic-bezier(0.4,0,0.2,1)',
                    transform: selectedGoal === goal.id ? 'scale(1.02)' : 'scale(1)'
                  }}
                >
                  <div style={{ transform: 'scale(0.8)' }}>
                    {goal.icon}
                  </div>
                  <span style={{ fontSize: '1rem', fontWeight: '500', textAlign: 'center' }}>
                    {goal.title}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {!authCallbackUrl && marketingUrl && (
            <div className="mt-8">
              <a href={marketingUrl} className="confirm-button" style={{ maxWidth: '300px', display: 'inline-block' }}>
                Continue to Homebot
              </a>
            </div>
          )}

          {authCallbackUrl && (
            <div className="mt-8">
              <button
                onClick={() => redirectToHomebot(authCallbackUrl)}
                className="confirm-button"
                style={{ maxWidth: '300px' }}
              >
                Continue to Homebot
              </button>
              <p className="mt-2 text-sm opacity-75">
                Or wait 5 seconds to be automatically redirected
              </p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Success; 
